/**
 * Pure domain logic for data center thermal overlay and ASHRAE color scaling.
 * Thresholds:
 *   < 25°C   : Cool / Normal (ASHRAE A1-A4 optimal) -> Blue/Cyan
 *   25..30°C : Warm / Elevated -> Amber
 *   > 30°C   : Critical / Hotspot -> Red
 */

export interface ThermalColorStyle {
  fill: string;
  stroke: string;
  badgeBg: string;
  label: string;
}

export const THERMAL_THRESHOLDS = {
  COOL_MAX: 25,
  WARM_MAX: 30,
} as const;

export const THERMAL_COLORS: Record<'cool' | 'warm' | 'hot', ThermalColorStyle> = {
  cool: {
    fill: 'rgba(2, 132, 199, 0.30)',
    stroke: '#0284c7',
    badgeBg: 'rgba(2, 132, 199, 0.20)',
    label: 'Норма (<25°C)',
  },
  warm: {
    fill: 'rgba(245, 158, 11, 0.30)',
    stroke: '#f59e0b',
    badgeBg: 'rgba(245, 158, 11, 0.20)',
    label: 'Нагрев (25–30°C)',
  },
  hot: {
    fill: 'rgba(239, 68, 68, 0.38)',
    stroke: '#ef4444',
    badgeBg: 'rgba(239, 68, 68, 0.25)',
    label: 'Перегрев (>30°C)',
  },
};

/**
 * Maps a numeric Celsius temperature to thermal overlay styling.
 * Returns null if temp is null, undefined, or NaN (indicating no telemetry available).
 */
export function getThermalColor(temp: number | null | undefined): ThermalColorStyle | null {
  if (temp == null || !Number.isFinite(temp)) {
    return null;
  }
  if (temp < THERMAL_THRESHOLDS.COOL_MAX) {
    return THERMAL_COLORS.cool;
  }
  if (temp <= THERMAL_THRESHOLDS.WARM_MAX) {
    return THERMAL_COLORS.warm;
  }
  return THERMAL_COLORS.hot;
}

function parseNumeric(val: any): number | null {
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  if (typeof val === 'string' && val.trim() !== '') {
    const num = parseFloat(val);
    return Number.isFinite(num) ? num : null;
  }
  return null;
}

/**
 * Resolves the temperature for a specific rack from telemetry data (either dictionary or array).
 */
export function getRackTemperature(
  rack: { id?: number | string; row_index?: number; rack_index?: number },
  telemetryMap?: Record<string | number, any> | Array<any> | null
): number | null {
  if (!telemetryMap || !rack) return null;

  // Case 1: telemetryMap is an Array of records (e.g. from /api/datacenters/{code}/telemetry)
  if (Array.isArray(telemetryMap)) {
    const item = telemetryMap.find(t =>
      (rack.id != null && t.rack_id != null && String(t.rack_id) === String(rack.id)) ||
      (rack.row_index != null && rack.rack_index != null &&
       t.row_index === rack.row_index && t.rack_index === rack.rack_index)
    );
    if (!item) return null;
    return parseNumeric(item.temperature ?? item.temp_c ?? item.val);
  }

  // Case 2: telemetryMap is a Record/dictionary (e.g. keyed by id or "row-rack")
  const keyById = rack.id != null ? telemetryMap[rack.id] ?? telemetryMap[String(rack.id)] : undefined;
  const keyByCoords = rack.row_index != null && rack.rack_index != null
    ? telemetryMap[`${rack.row_index}-${rack.rack_index}`]
    : undefined;

  const raw = keyById ?? keyByCoords;
  if (raw == null) return null;

  const directNum = parseNumeric(raw);
  if (directNum !== null) {
    return directNum;
  }
  return parseNumeric(raw.temperature ?? raw.temp_c ?? raw.val);
}
